import { useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { KmxShareStroked } from '@kmx/mui-icons';
import { IRecentSearchTile } from '../../utilities/types';
import addQueryParam from '../../utilities/addQueryParam';
import { ITrackingPayload } from '../../../../types/analytics';
import { ANALYTICS_CONSTANTS, buildModuleExperience, trackEDDL } from '../../../../utilities/analytics';
import TileKebabMenuItemSnackbar from '../../hero/tile-kebab-menu/TileKebabMenuItemSnackbar';
import Icons from '../../../../utilities/icons';
import KebabMenu, { IKebabMenuItem } from '../../../../components/KebabMenu';

interface IMobileRecentSearchTileProps {
    recentSearchTile: IRecentSearchTile;
}

const MobileRecentSearchTile = ({
    recentSearchTile: {
        title,
        link,
        newMatchCountText,
        newMatchCount,
        inventoryCount,
        inventoryCountText,
        imageUrl,
        imageAlt,
    },
}: IMobileRecentSearchTileProps) => {
    const [isShareSnackbarOpen, setIsShareSnackbarOpen] = useState<boolean>(false);

    const handleTileClick = (title: string, inventoryCount: number) => {
        const payload: ITrackingPayload = {
            event: ANALYTICS_CONSTANTS.EVENTS.CLICK.RECOMMENDED_CARTILE,
            linkDetails: {
                name: String(inventoryCount),
                position: title,
            },
            module: {
                moduleExperience: buildModuleExperience('VR|Homepage|Searched Tiles'),
            },
        };
        trackEDDL(payload);
    };

    const handleShareClick = async () => {
        await navigator.clipboard.writeText(`${window.location.origin}${link}`);
        setIsShareSnackbarOpen(true);
    };

    const KEBAB_MENU_ITEMS: IKebabMenuItem[] = [
        {
            text: 'Share',
            icon: <KmxShareStroked color="secondary" fontSize="small" />,
            handleClick: (e) => {
                e.preventDefault();
                handleShareClick();
                const payload: ITrackingPayload = {
                    event: ANALYTICS_CONSTANTS.EVENTS.CLICK.CTA,
                    linkDetails: {
                        name: 'share',
                        position: `Searched:${title}`,
                    },
                };
                trackEDDL(payload);
            },
            href: '#share',
        },
    ];

    return (
        <Paper
            elevation={0}
            sx={{
                width: 200,
                height: 200,
                borderRadius: 1.5,
                overflow: 'hidden',
                position: 'relative',
                padding: '1rem',
            }}
        >
            <Link
                href={!newMatchCount ? link : addQueryParam(link, 'newMatch=true')}
                onClick={() => {
                    handleTileClick(title, inventoryCount);
                }}
                underline="none"
                data-ignore-anchor-tag-rule
            >
                <Box
                    component="img"
                    src={imageUrl}
                    alt={imageAlt}
                    sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        width: 'calc(100% + 3.5rem)',
                        transform: 'translate(-1rem,2rem)',
                    }}
                />
                <Box
                    sx={{
                        height: '100%',
                        width: '100%',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        //TODO : Change this #F2F3F5 reference to pallette.background.subdued when available
                        background: 'linear-gradient(-180deg, #F2F3F5 25%, rgba(240, 241, 242, 0) 50%)',
                    }}
                />
                <Stack direction="column" sx={{ position: 'relative' }}>
                    <Typography component="h6" variant="h6" color="textPrimary" noWrap>
                        {title}
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ height: '1.5rem' }}>
                        <Typography variant="body2" color="textSecondary">
                            {inventoryCountText}
                        </Typography>
                        {newMatchCountText && <Chip color="flagged" size="small" label={newMatchCountText} />}
                    </Stack>
                </Stack>
            </Link>

            <Box sx={{ position: 'absolute', bottom: 8, right: 8 }}>
                <KebabMenu category={title}>
                    {KEBAB_MENU_ITEMS.map(({ handleClick, icon, text }) => (
                        <MenuItem key={text} onClick={(e: React.MouseEvent<HTMLLIElement>) => handleClick(e)}>
                            <ListItemIcon>{icon}</ListItemIcon>
                            <ListItemText>{text}</ListItemText>
                        </MenuItem>
                    ))}
                </KebabMenu>
            </Box>

            <TileKebabMenuItemSnackbar
                isOpen={isShareSnackbarOpen}
                textLabel="Link copied to your clipboard"
                icon={Icons.share}
                cancelAction={() => setIsShareSnackbarOpen(false)}
            />
        </Paper>
    );
};

export default MobileRecentSearchTile;
